/**
 * Ein einfaches I18N Framework für den Client, ein gleichnamiger Gegenpart existiert serverseitig für PHP.
 * Das Format der Sprachtexte ist JSON:
 *
 * <pre><code>
 * languageStore = {
 *    de: {
 *      __shortname: 'de',
 *      number_dec_point: ',',
 *      number_thousands_sep: '.',
 *      // hier die Sprachtexte als key-value mit optionalen Platzhaltern
 *      HelloXY: 'Hallo %s!',
 *      // weitere Keys
 *    }
 * }
 * </pre></code>
 *
 * Eine Übersetzung erhält man so:
 *
 * <pre><code>
 *     als globale Javascript-Funktion: I18N('HelloXY','Peter')
 * </pre></code>
 *
 * Der Languagestore sollte serverseitig beim Erzeugen des HTMLs als JSON ausgegeben werden und der Methode
 * I18N.setSingleLanguageStore(store) zugewiesen werden.
 *
 * __Tipp__: man kann alle I18N Texte eines Projektes mit folgendem node-Skript finden, das man im Basis-Ordner des Projektes aufruft:
 * [node oc-base/scripts/create_i18n_keys.js](https://github.com/OC-Git/oc-base/blob/main/oc-build/scripts/create_i18n_keys.js)
 */

/**
 * imports
 */
import sprintf from "sprintf-js";
import { Urls } from "oc-commons/Urls";
import * as Commons from "oc-commons/Commons";
import _ from "lodash";
import { relaxedParseDecimalNumber } from "./relaxedParseDecimalNumber";

const vsprintf = sprintf.vsprintf;

/* =====================================================================================================================
 * Types & Interfaces (Strict No Any)
 * ===================================================================================================================== */

/**
 * Represents the structure of a language definition.
 */
export interface ILanguageMap {
  __shortname: string;
  number_dec_point: string;
  number_thousands_sep: string;
  [key: string]: string;
}

/**
 * Dictionary of languages, keyed by ISO code.
 */
export interface ILanguageStore {
  [iso: string]: ILanguageMap;
}

/**
 * Minimal interface for the global APP object as used in this file.
 */
interface IApp {
  dirty: () => void;
  settings?: {
    currency?: string;
  };
}

declare const APP: IApp;

/**
 * Minimal interface for the global Doms object as used in this file.
 */
interface IDoms {
  showIf: (element: Element, condition: boolean) => void;
}

declare const Doms: IDoms;

/**
 * Interface for the Webpack RequireContext (used in slurpStore/slurpDefaults).
 */
export interface IRequireContext {
  keys(): string[];
  (id: string): any;
  resolve(id: string): string;
  id: string;
}

/* =====================================================================================================================
 * State
 * ===================================================================================================================== */

/**
 * Default-Store
 */
let languageStore: ILanguageStore = {
  de: {
    __shortname: "de",
    number_dec_point: ",",
    number_thousands_sep: ".",
  },
  en: {
    __shortname: "en",
    number_dec_point: ".",
    number_thousands_sep: ",",
  },
};

let lang: string = "de";
const missedAlreadyLogged: Set<string> = new Set();

let isResponsiveView: boolean = false;
let responsiveQuery: MediaQueryList | null = null;
let responsiveBreakpointPx: number = 800; // Default
let showKeysMode: boolean = false;

function updateResponsiveState(): void {
  if (!responsiveQuery) return;
  const newState = responsiveQuery.matches;
  if (newState !== isResponsiveView) {
    isResponsiveView = newState;
    // Re-Render aller I18N Texte bei Änderung
    if (typeof I18N.refreshElement === "function" && document?.body) {
      I18N.refreshElement(document.body);
      APP.dirty();
    }
  }
}

function initResponsiveQuery(): void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;

  if (responsiveQuery) {
    responsiveQuery.removeEventListener("change", updateResponsiveState);
  }
  responsiveQuery = window.matchMedia(`(max-width: ${responsiveBreakpointPx}px)`);
  responsiveQuery.addEventListener("change", updateResponsiveState);
  isResponsiveView = responsiveQuery.matches;
}

/* =====================================================================================================================
 * Namespace helpers
 * ===================================================================================================================== */

const NAMESPACE_SEPARATOR = "::";

/**
 * Extracts the fallback key from a namespaced key.
 * e.g. "aria::Speichern" → "Speichern", "foo" → null
 */
function namespaceFallbackKey(key: string): string | null {
  const idx = key.indexOf(NAMESPACE_SEPARATOR);
  if (idx < 0) return null;
  return key.substring(idx + NAMESPACE_SEPARATOR.length);
}

/**
 * Looks up a key (with optional namespace-fallback) in the given language map.
 * Returns the resolved text or null if nothing was found.
 */
function resolveText(languageMap: ILanguageMap | undefined, key: string): string | null {
  if (!languageMap) return null;

  // 1. responsive variant
  if (isResponsiveView) {
    const responsiveText = languageMap["_responsive_" + key];
    if (responsiveText != null) return responsiveText;
  }

  // 2. exact key
  const exact = languageMap[key];
  if (exact != null) return exact;

  // 3. namespace fallback: strip namespace prefix and try bare key
  const fallback = namespaceFallbackKey(key);
  if (fallback != null) {
    if (isResponsiveView) {
      const responsiveFb = languageMap["_responsive_" + fallback];
      if (responsiveFb != null) return responsiveFb;
    }
    const fbText = languageMap[fallback];
    if (fbText != null) return fbText;
  }

  return null;
}

/* =====================================================================================================================
 * I18N Implementation
 * ===================================================================================================================== */

/**
 * Gibt den in die aktuelle Sprache übersetzten Text zu einem Sprachkey zurück. Optional können weitere Parameter angegeben werden,
 * die mittels der sprintf Syntax im Sprachtext referenziert werden können.
 * @example
 * I18N(key,[parameter1],[parameter2],...) die optionalen Parameter werden an vsprintf übergeben,
 * so dass im Message-Value z.B. %s als Platzhalter verwendet werden kann.
 */
export const I18N: II18NFunction = function (...args: any[]) {
  // Support passing an array as the first argument (e.g. I18N(['key', 'param'])) through spread detection logic
  // Although the function signature uses ...args, we manually inspect args[0] to support the existing JS logic.
  let activeArgs = args;
  if (_.isArray(args[0])) {
    activeArgs = args[0] as any[];
  }
  const key = activeArgs[0];
  if (Commons.isEmpty(key)) {
    return "";
  }
  const languageMap = languageStore[lang];
  const text = resolveText(languageMap, key);
  if (text == null) {
    if (!missedAlreadyLogged.has(key)) {
      missedAlreadyLogged.add(key);
      console.warn(`lang=${lang} missing language key "${key}"`);
    }
    //          für das Backend erstmal rausgenommen wegen Abwärtskompatibilität (sonst müssten alle Texte immer übersetzt werden...)
    //         return 'I18N("' + key + '",' + args.slice(1).join(',') + ')';
    //      das hier setzt vorraus dass der key dem Standard-Text entspricht, der unübersetzt ausgegeben würde, also mit Platzhalter.
    //      das ist best practise - nimm einfach den originalen Text und mach' dir keine Gedanken über künstliche Keys...
    let label = key;
    if (activeArgs.length > 1) {
      label += "│" + activeArgs.slice(1).join("│");
    }
    return "〖" + label + "〗";
  }
  try {
    const result = vsprintf(text, activeArgs.slice(1));
    if (showKeysMode) {
      return `[${key}]`;
    }
    return result;
  } catch (e) {
    const errorText = 'error for I18N key "' + key + '" for language "' + lang + '": ' + e;
    console.warn(errorText, e);
    return errorText;
  }
} as II18NFunction;

/**
 * Interface definition for the I18N function object including its static methods.
 */
export interface II18NFunction {
  (...args: any[]): string; // Using any[] for args because of the complex spread/array logic, but internally handled.
  // Ideally: (key: string | any[], ...params: any[]): string;

  getKeyValueMap: () => ILanguageMap | undefined;
  setKeyValueMap: (languageKeyValueMap: ILanguageMap) => void;
  setStore: (store: ILanguageStore) => ILanguageStore;
  slurpStore: (requireContext: IRequireContext) => ILanguageStore;
  setDefaults: (store: ILanguageStore) => ILanguageStore;
  slurpDefaults: (requireContext: IRequireContext) => ILanguageStore;
  getLanguage: () => string;
  setLanguage: (languageIso2Code: string) => void;
  autodetectLanguage: () => void;
  refreshElement: (element: Element) => void; // Using Element which covers HTMLElement
  hasKey: (key: string) => boolean;
  formatFloat: (number: number, digits?: number) => string;
  parseFloat: (numberString: string | number, max?: number) => number;
  formatCurrency: (value: number | null | undefined, digits?: number) => string;
  setResponsiveBreakpoint: (px: number) => void;
  setShowKeys: (enabled: boolean) => void;
  isShowKeys: () => boolean;
  hasMissing: boolean;
}

/**
 * Gibt die KeyValue-Map für die aktuelle Sprache zurück
 */
I18N.getKeyValueMap = (): ILanguageMap | undefined => languageStore[lang];

/**
 * Setzt die KeyValue-Map für die Sprache entsprechend languageKeyValueMap.__shortname und setzt die Sprache auf languageKeyValueMap.__shortname
 */
I18N.setKeyValueMap = (languageKeyValueMap: ILanguageMap) => {
  if (languageKeyValueMap.__shortname) {
    lang = languageKeyValueMap.__shortname;
  }
  const store: ILanguageStore = {};
  store[lang] = languageKeyValueMap;
  I18N.setStore(store);
};

/**
 * Setzt den kompletten Sprachstore für alle Sprachen.
 * Defaults werden überschrieben, wenn im Store entsprechende Werte sind, ansonsten bleiben die Defaults bestehen.
 */
I18N.setStore = (store: ILanguageStore) =>
  (languageStore = Commons.defaultsDeepSafe({}, store, languageStore));
I18N.slurpStore = (requireContext: IRequireContext) =>
  I18N.setStore(Commons.slurpDirectory(requireContext));

/**
 * Fügt Default-Werte für alle Sprachen hinzu. Bestehende Werte werden nicht überschrieben.
 */
I18N.setDefaults = (store: ILanguageStore) => Commons.defaultsDeepSafe(languageStore, store);
I18N.slurpDefaults = (requireContext: IRequireContext) =>
  I18N.setDefaults(Commons.slurpDirectory(requireContext));

/**
 * gibt den ISO2 - Code der aktuellen Sprache zurück, z.B. "de" oder "de-DE"
 */
I18N.getLanguage = function (): string {
  return lang;
};

/**
 * setzt den aktuellen ISO2-Code der Sprache, also z.B. "de" oder "de_DE"
 */
I18N.setLanguage = function (languageIso2Code: string | null | undefined) {
  if (!languageIso2Code) {
    console.warn("I18N.setLanguage: languageIso2Code may not be null or undefined!");
    return;
  }
  const languageMap = languageStore[languageIso2Code];
  if (languageMap) {
    if (!languageMap.__shortname) {
      languageMap.__shortname = languageIso2Code;
    }
    lang = languageMap.__shortname;
  } else {
    console.warn(
      'I18N.setLanguage: there exists no language in store for isocode "' + languageIso2Code + '"',
    );
  }
};

/**
 * erkennt die im Browser eingestellte Sprache
 */
I18N.autodetectLanguage = function () {
  const param = Urls.urlParam("lang");
  let languageIso2Code: string | null | undefined = typeof param === "string" ? param : null;

  if (!languageIso2Code) {
    languageIso2Code = detectBrowserLanguage();
  }
  I18N.setLanguage(mapToKnownLanguage(languageIso2Code));
};

function detectBrowserLanguage(): string | null {
  let language: string | null | undefined = null;
  const nav = navigator as any; // Navigator types often miss legacy properties like browserLanguage

  // Custom detection matching the regex
  if (nav && nav.userAgent) {
    const match = nav.userAgent.match(/android.*\W(\w\w)-(\w\w)\W/i);
    if (match) {
      language = match[1];
    }
  }

  if (!language && nav) {
    if (nav.language) {
      language = nav.language;
    } else if (nav.browserLanguage) {
      language = nav.browserLanguage;
    } else if (nav.systemLanguage) {
      language = nav.systemLanguage;
    } else if (nav.userLanguage) {
      language = nav.userLanguage;
    }
    if (language) {
      language = language.substr(0, 2);
    }
  }

  if (language == null) {
    language = lang;
  } else {
    let found: string | null = null;
    // _.keys returns string[], and _.each iterates it
    _.each(_.keys(languageStore), function (iso) {
      // safe check if language is string
      if (typeof language === "string" && language.toLowerCase().indexOf(iso.toLowerCase()) == 0) {
        found = iso;
      }
    });
    language = found == null ? lang : found;
  }
  return language;
}

function mapToKnownLanguage(languageIso2Code: string | null | undefined): string {
  if (languageIso2Code == null) {
    return lang;
  }
  if (languageIso2Code in languageStore) {
    return languageIso2Code;
  }
  const splitLang = languageIso2Code.split("[_- ]")[0];
  if (splitLang in languageStore) {
    return splitLang;
  }
  return lang;
}

/**
 * Ersetzt im übergebenen DOM-Element alle speziell ausgezeichneten Element-Inhalte bzw. Attribute.
 * @param {Element} element
 * @example
 * &lt;div data-i18n=&quot;key&quot;&gt;&lt;/div&gt; ersetzt den Inhalt des Elementes
 * &lt;div data-visibleForLanguage=&quot;de&quot;&gt;&lt;/div&gt; zeigt das Element nur an, wenn die Sprache "de" ist
 * &lt;input data-i18n-placeholder=&quot;key&quot;&gt; ersetzt Placeholder des Input-Feldes
 * &lt;input data-i18n-value=&quot;key&quot;&gt; ersetzt Value des Input-Feldes
 */
I18N.refreshElement = function (element: Element) {
  element.querySelectorAll("*[data-i18n]").forEach((el) => {
    const key = el.getAttribute("data-i18n");
    if (key) {
      const text = I18N(key);
      el.innerHTML = text;
    }
  });
  element.querySelectorAll("*[data-visibleForLanguage]").forEach((el) => {
    const iso = el.getAttribute("data-visibleForLanguage");
    Doms.showIf(el, iso == lang);
  });
  element.querySelectorAll("*[data-i18n-placeholder]").forEach((el) => {
    const key = el.getAttribute("data-i18n-placeholder");
    if (key) {
      const text = I18N(key);
      el.setAttribute("placeholder", text);
    }
  });
  element.querySelectorAll("*[data-i18n-value]").forEach((el) => {
    const key = el.getAttribute("data-i18n-value");
    if (key) {
      const text = I18N(key);
      el.setAttribute("value", text);
    }
  });
};

/**
 * Prüft nach ob ein Key für die aktuelle Sprache existiert.
 * Berücksichtigt Namespace-Fallback: Falls "aria::Speichern" nicht existiert,
 * wird geprüft ob "Speichern" existiert.
 */
I18N.hasKey = function (key: string): boolean {
  const languageMap = languageStore[lang];
  if (languageMap == null) {
    return false;
  }
  if (languageMap[key] != null) {
    return true;
  }
  // Namespace fallback
  const fallback = namespaceFallbackKey(key);
  if (fallback != null && languageMap[fallback] != null) {
    return true;
  }
  return false;
};

/**
 * formatiert eine Zahl im aktuellen lokalen Format
 * @param number
 * @param {number} [digits=2] optional, Anzahl der Nachkommastellen
 * @returns {string}
 */
I18N.formatFloat = function (number: number, digits: number = 2): string {
  if (typeof number !== "number") {
    // Original returned string usually, but if input is invalid? code said return ""
    return "";
  }
  return number.toLocaleString(I18N.getLanguage(), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

/**
 * parsed einen Zahlstring im aktuellen lokalen Format
 */
I18N.parseFloat = function (numberString: string | number, max?: number): number {
  if (typeof numberString === "number") {
    return numberString;
  }
  let { number_thousands_sep, number_dec_point } = I18N.getKeyValueMap();
  return relaxedParseDecimalNumber(numberString, number_thousands_sep, number_dec_point, max);
};

/**
 * formatiert eine Zahl als Geldbetrag
 * @param {number} value
 * @param {number} [digits]
 * @returns {string}
 */
I18N.formatCurrency = (value: number | null | undefined, digits?: number): string => {
  const options: Intl.NumberFormatOptions = {
    style: "currency",
    currency: APP?.settings?.currency ?? "EUR",
    currencyDisplay: "symbol",
    useGrouping: true,
  };
  if (Number.isInteger(digits)) {
    options.minimumFractionDigits = digits;
    options.maximumFractionDigits = digits;
  }
  // Original logic: if (value == null || !_.isNumber(value)) value = 0;
  // _.isNumber checks for typeof number AND not NaN ideally, or just typeof number?
  // lodash isNumber returns true for NaN too.
  let safeValue = value;
  if (safeValue == null || !_.isNumber(safeValue)) {
    safeValue = 0;
  }
  return safeValue.toLocaleString(I18N.getLanguage(), options);
};

/**
 * Setzt den Breakpoint (in Pixeln), ab dem responsive Texte verwendet werden sollen.
 * @param {number} px
 */
I18N.setResponsiveBreakpoint = function (px: number) {
  if (!_.isNumber(px) || px <= 0) {
    console.warn("I18N.setResponsiveBreakpoint(px): px muss eine positive Zahl sein!");
    return;
  }
  responsiveBreakpointPx = px;
  initResponsiveQuery();
};

initResponsiveQuery();

/**
 * Aktiviert oder deaktiviert den "Show Keys"-Modus, in dem nur die I18N-Keys angezeigt werden.
 * Gedacht für eingeloggte Admins zur Analyse der I18N-Keys.
 * Kann über den URL-Parameter ?showI18NKeys aktiviert werden. Der Modus wird per Cookie persistiert,
 * damit auch Backend-API-Requests (z.B. für Metamodell-Daten und PDFs) den Modus erkennen.
 * Zum Deaktivieren: ?showI18NKeys=off
 */
I18N.setShowKeys = function (enabled: boolean) {
  showKeysMode = enabled;
  if (typeof document !== "undefined") {
    if (enabled) {
      document.cookie = "showI18NKeys=1;path=/";
    } else {
      document.cookie = "showI18NKeys=;path=/;max-age=0";
    }
  }
};

I18N.isShowKeys = function (): boolean {
  return showKeysMode;
};

// Auto-detect showI18NKeys: URL-Parameter setzt Cookie, fehlender Parameter löscht Cookie
if (typeof window !== "undefined") {
  const showKeysParam = Urls.urlParam("showI18NKeys");
  if (showKeysParam !== null && showKeysParam !== undefined && showKeysParam !== false) {
    if (showKeysParam === "off" || showKeysParam === "0" || showKeysParam === "false") {
      I18N.setShowKeys(false);
    } else {
      I18N.setShowKeys(true);
    }
  } else {
    // Kein URL-Parameter: Cookie löschen falls vorhanden
    if (typeof document !== "undefined" && document.cookie.includes("showI18NKeys=1")) {
      document.cookie = "showI18NKeys=;path=/;max-age=0";
    }
  }
}

I18N.hasMissing = false;

/**
 * für einfachen globalen Zugriff auf das I18N-Objekt
 */
// Use Type Assertion for self to avoid strict type error if Window is not perfectly aligned
(self as any)["I18N"] = I18N;
